import React, { useState, useContext } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import { buildChartOption } from './resolutionsChartStyles';

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  CanvasRenderer
]);

const ResolutionsChart = ({
  hourlyData = [],
  weeklyTrend = [],
  isLoading = false,
  isWeeklyLoading = false,
  onRequireWeekly
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [viewMode, setViewMode] = useState('hoy'); // 'hoy' por defecto

  const handleModeChange = mode => {
    setViewMode(mode);
    if (mode === 'semanal' && onRequireWeekly) {
      onRequireWeekly();
    }
  };

  const now = new Date();
  const currentHour = now.getHours();
  const currentHourKey = `${currentHour.toString().padStart(2, '0')}:00`;

  // Calcular el rango horario visible para HOY (desde las 08:00 hasta el máximo de la hora actual o datos)
  const startHour = 9;
  let maxDataHour = startHour;
  if (Array.isArray(hourlyData)) {
    hourlyData.forEach(item => {
      const h = item.iHora ?? item.hora;
      if (
        h !== undefined &&
        h !== null &&
        ((item.iCreados ?? 0) > 0 || (item.iPendientes ?? 0) > 0 || (item.iResueltos ?? 0) > 0)
      ) {
        if (h > maxDataHour) maxDataHour = h;
      }
    });
  }
  const endHour = Math.max(12, currentHour, maxDataHour);
  const jornadaText = `${startHour.toString().padStart(2, '0')}:00 – ${endHour.toString().padStart(2, '0')}:00`;

  const getOption = () => {
    if (viewMode === 'hoy') {
      const categories = [];
      const hoursMap = {};
      for (let h = startHour; h <= endHour; h++) {
        const key = `${h.toString().padStart(2, '0')}:00`;
        categories.push(key);
        hoursMap[key] = { creados: 0, pendientes: 0, resueltos: 0 };
      }

      if (Array.isArray(hourlyData)) {
        hourlyData.forEach(item => {
          const h = item.iHora ?? item.hora;
          if (h !== undefined && h !== null) {
            const key = `${h.toString().padStart(2, '0')}:00`;
            if (hoursMap[key]) {
              hoursMap[key].creados += (item.iCreados ?? item.creados ?? 0);
              hoursMap[key].pendientes += (item.iPendientes ?? item.pendientes ?? 0);
              hoursMap[key].resueltos += (item.iResueltos ?? item.resueltos ?? item.iCantidad ?? 0);
            }
          }
        });
      }

      const dataCreados = categories.map(k => hoursMap[k].creados);
      const dataPendientes = categories.map(k => hoursMap[k].pendientes);
      const dataResueltos = categories.map(k => hoursMap[k].resueltos);

      return buildChartOption(categories, dataCreados, dataResueltos, dataPendientes, currentHourKey, isDark);
    }

    // Modo Tendencia Semanal (Últimos 7 días)
    const validTrend = Array.isArray(weeklyTrend) && weeklyTrend.length > 0
      ? weeklyTrend
      : [];

    const xLabels = validTrend.map(d => {
      const nombre = d.vNombreDia || '';
      const fecha = d.vFecha ? d.vFecha.slice(5) : '';
      return nombre ? `${nombre.slice(0, 3)} ${fecha}` : fecha;
    });

    const defaultLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    const categories = xLabels.length > 0 ? xLabels : defaultLabels;
    const dataCreados = validTrend.map(d => d.iCreados ?? d.creados ?? 0);
    const dataPendientes = validTrend.map(d => d.iPendientes ?? d.pendientes ?? 0);
    const dataResueltos = validTrend.map(d => d.iResueltos ?? d.resueltos ?? 0);

    return buildChartOption(categories, dataCreados, dataResueltos, dataPendientes, null, isDark);
  };

  return (
    <div className="se-card-panel">
      {/* Encabezado superior: Título y Tabs de Alternancia */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="se-chart-title">
            {viewMode === 'hoy' ? 'Actividad de Hoy' : 'Tendencia Semanal'}
          </h5>
          <span className="se-chart-subtitle">
            {viewMode === 'hoy'
              ? 'Tickets creados, pendientes y resueltos por hora'
              : 'Tickets creados, pendientes y resueltos (últimos 7 días)'}
          </span>
        </div>

        {/* View toggle tabs */}
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn btn-sm se-chart-btn ${viewMode === 'hoy' ? (isDark ? 'btn-dark border border-secondary active' : 'btn-light border active') : 'btn-outline-secondary'}`}
            onClick={() => handleModeChange('hoy')}
          >
            Hoy
          </button>
          <button
            type="button"
            className={`btn btn-sm se-chart-btn ${viewMode === 'semanal' ? (isDark ? 'btn-dark border border-secondary active' : 'btn-light border active') : 'btn-outline-secondary'}`}
            onClick={() => handleModeChange('semanal')}
            disabled={isWeeklyLoading}
          >
            {isWeeklyLoading ? 'Cargando...' : 'Semana'}
          </button>
        </div>
      </div>

      {/* Fila de Leyenda superior: Creados (Azul) / Pendientes (Ámbar) / Resueltos (Verde) y Jornada (Derecha) */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-1 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: '12px',
                height: '10px',
                backgroundColor: '#2563eb',
                borderRadius: '3px',
                display: 'inline-block'
              }}
            />
            <span className="text-800" style={{ fontSize: '0.84rem', fontWeight: 500 }}>
              Creados
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: '12px',
                height: '10px',
                backgroundColor: '#f59e0b',
                borderRadius: '3px',
                display: 'inline-block'
              }}
            />
            <span className="text-800" style={{ fontSize: '0.84rem', fontWeight: 500 }}>
              Pendientes
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: '12px',
                height: '10px',
                backgroundColor: '#059669',
                borderRadius: '3px',
                display: 'inline-block'
              }}
            />
            <span className="text-800" style={{ fontSize: '0.84rem', fontWeight: 500 }}>
              Resueltos
            </span>
          </div>
        </div>

        <div>
          <span style={{ fontSize: '0.84rem', color: '#94a3b8' }}>
            {viewMode === 'hoy' ? 'Jornada ' : 'Período '}
            <strong className="text-700" style={{ fontWeight: 600 }}>
              {viewMode === 'hoy' ? jornadaText : 'Últimos 7 días'}
            </strong>
          </span>
        </div>
      </div>

      {/* Caja contenedora redondeada con borde suave para la gráfica */}
      <div
        className="se-chart-inner-box"
        style={{
          border: isDark ? '1px solid #2b3035' : '1px solid #eef2f6',
          borderRadius: '14px',
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          padding: '10px 14px',
          minHeight: '270px'
        }}
      >
        <div className="se-resolutions-chart-wrapper">
          <ReactEChartsCore
            echarts={echarts}
            option={getOption()}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ResolutionsChart;
